import { useEffect, useState } from "react";
import { api, type MediaItem, type Playlist, type Screen } from "../api";
import { Icon } from "../icons";
import { relativeTime } from "../format";
import { Banner, StatusPill } from "../ui";

export function HomePage({ navigate }: { navigate: (to: string) => void }) {
  const [screens, setScreens] = useState<Screen[] | null>(null);
  const [playlists, setPlaylists] = useState<Playlist[] | null>(null);
  const [mediaCount, setMediaCount] = useState<number | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let stop = false;
    async function run() {
      try {
        const [screenData, playlistData, mediaData] = await Promise.all([
          api<{ screens: Screen[] }>("/api/screens"),
          api<{ playlists: Playlist[] }>("/api/playlists"),
          api<{ media: MediaItem[] }>("/api/media"),
        ]);
        if (stop) return;
        setScreens(screenData.screens);
        setPlaylists(playlistData.playlists);
        setMediaCount(mediaData.media.filter((item) => item.status === "ready").length);
        setError("");
      } catch (reason) {
        if (!stop) setError(reason instanceof Error ? reason.message : "Chargement impossible");
      }
    }
    void run();
    const timer = window.setInterval(() => void run(), 30000);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, []);

  const ready = screens !== null && playlists !== null && mediaCount !== null;
  const online = screens?.filter((screen) => screen.status === "online").length ?? 0;
  const offline = screens?.filter((screen) => screen.status === "offline").length ?? 0;
  const pending = screens?.reduce((total, screen) => total + screen.devices.filter((device) => device.status === "pending").length, 0) ?? 0;
  const dropped = screens?.filter((screen) => screen.dropped) ?? [];
  const today = new Intl.DateTimeFormat("fr-CA", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "America/Toronto",
  }).format(new Date());
  const dateLabel = today.charAt(0).toUpperCase() + today.slice(1);

  const steps = ready
    ? [
        { id: "screen", done: screens.length > 0, title: "Créer un écran", hint: "Un nom, par exemple Cardio.", href: "/ecrans" },
        { id: "media", done: mediaCount > 0, title: "Envoyer un média", hint: "Vidéo MP4 ou image.", href: "/medias" },
        { id: "playlist", done: playlists.some((playlist) => playlist.items.length > 0), title: "Monter une playlist", hint: "L'ordre de passage sur la télé.", href: "/playlists" },
        { id: "assign", done: screens.some((screen) => screen.assignment), title: "Assigner la playlist", hint: "Sans playlist, la télé reste noire.", href: "/ecrans" },
        { id: "allow", done: screens.some((screen) => screen.devices.some((device) => device.status === "approved")), title: "Autoriser la télé", hint: "Le code à l'écran doit être le même.", href: "/autorisations" },
      ]
    : [];
  const nextStep = steps.find((step) => !step.done);
  const setupDone = ready && !nextStep;

  return (
    <section>
      <header className="page-head">
        <div>
          <p className="eyebrow">{dateLabel}</p>
          <h1>Aujourd'hui</h1>
          <p className="lede">L'état des télés du club, et ce qu'il reste à brancher.</p>
        </div>
      </header>
      {error ? <Banner tone="error">{error}</Banner> : null}
      {!ready ? (
        <div className="skeleton-grid" aria-hidden="true">
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
      ) : (
        <>
          {dropped.length > 0 ? (
            <div className="callout alert">
              <Icon name="alert" />
              <div>
                <strong>
                  {dropped.length === 1
                    ? `${dropped[0]?.name ?? "Une télé"} ne répond plus.`
                    : `${dropped.length} télés ne répondent plus.`}
                </strong>
                <p>Vérifiez qu'elles sont allumées et reliées au réseau.</p>
              </div>
              <button type="button" onClick={() => navigate("/ecrans#hors-ligne")}>Voir</button>
            </div>
          ) : null}
          {pending > 0 ? (
            <div className="callout">
              <Icon name="shield" />
              <div>
                <strong>{pending === 1 ? "Une télé attend un code." : `${pending} télés attendent un code.`}</strong>
                <p>Comparez le code avec celui affiché sur la télé du club.</p>
              </div>
              <button type="button" className="primary" onClick={() => navigate("/autorisations")}>Autoriser</button>
            </div>
          ) : null}
          <div className="stat-grid">
            <button type="button" className="stat good" onClick={() => navigate("/ecrans#en-lecture")}>
              <span className="stat-value">{online}</span>
              <span className="stat-label">en lecture</span>
            </button>
            <button type="button" className={dropped.length > 0 ? "stat bad" : "stat"} onClick={() => navigate("/ecrans#hors-ligne")}>
              <span className="stat-value">{offline}</span>
              <span className="stat-label">hors ligne</span>
            </button>
            <button type="button" className={pending > 0 ? "stat warn" : "stat"} onClick={() => navigate("/autorisations")}>
              <span className="stat-value">{pending}</span>
              <span className="stat-label">à autoriser</span>
            </button>
            <button type="button" className="stat" onClick={() => navigate("/medias")}>
              <span className="stat-value">{mediaCount}</span>
              <span className="stat-label">{mediaCount === 1 ? "média" : "médias"}</span>
            </button>
          </div>
          {nextStep ? (
            <div className="block">
              <h2>Pour qu'une télé joue</h2>
              <ol className="checklist">
                {steps.map((step, index) => {
                  const isNext = step.id === nextStep.id;
                  return (
                    <li key={step.id} className={step.done ? "check-item done" : isNext ? "check-item next" : "check-item"}>
                      <span className="check-mark" aria-hidden="true">{step.done ? <Icon name="check" size={14} /> : index + 1}</span>
                      <span>
                        <strong>{step.title}</strong>
                        <span className="muted">{step.hint}</span>
                      </span>
                      {isNext ? (
                        <button type="button" className="primary" onClick={() => navigate(step.href)}>Faire</button>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </div>
          ) : null}
          {setupDone && dropped.length === 0 && pending === 0 ? (
            <p className="quiet-note">Tout est branché. Un changement de playlist s'applique à la fin du média en cours.</p>
          ) : null}
          <div className="block">
            <h2>Actions</h2>
            <div className="quick-grid">
              <button type="button" className="quick" onClick={() => navigate("/medias")}>
                <span className="quick-icon"><Icon name="upload" /></span>
                <span>
                  <strong>Envoyer un média</strong>
                  <span className="muted">Vidéo MP4 ou image</span>
                </span>
              </button>
              <button type="button" className="quick" onClick={() => navigate("/playlists")}>
                <span className="quick-icon"><Icon name="list" /></span>
                <span>
                  <strong>Monter une playlist</strong>
                  <span className="muted">L'ordre sur la télé</span>
                </span>
              </button>
              <button type="button" className="quick" onClick={() => navigate("/ecrans")}>
                <span className="quick-icon"><Icon name="screen" /></span>
                <span>
                  <strong>Régler un écran</strong>
                  <span className="muted">Lien, playlist, son</span>
                </span>
              </button>
            </div>
          </div>
          {screens.length > 0 ? (
            <div className="block">
              <div className="card home-list">
                <div className="split pad">
                  <h2>Télés</h2>
                  <button type="button" className="text-button" onClick={() => navigate("/ecrans")}>Tout voir</button>
                </div>
                {screens.map((screen) => (
                  <button key={screen.id} type="button" className="home-row" onClick={() => navigate(`/ecrans/${screen.id}`)}>
                    <span className="home-pills">
                      <StatusPill status={screen.status} />
                      {screen.dropped ? <span className="pill alert"><span className="dot" />Silence</span> : null}
                    </span>
                    <span className="home-copy">
                      <strong>{screen.name}</strong>
                      <span className="muted">
                        {screen.assignment?.playlistName ?? "Aucune playlist"}
                        {" · "}
                        {relativeTime(screen.lastSeen)}
                      </span>
                    </span>
                    <Icon name="chevron" />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
